import {
  Check,
  Eye,
  FileText,
  MagnifyingGlass,
  Pencil,
  PhoneCall,
  Trash,
  UserSound,
} from '@phosphor-icons/react';
import * as Sentry from '@sentry/browser';
import { useDebounce, useSessionStorageState } from 'ahooks';
import { getAuth } from 'firebase/auth';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { post } from 'src/api/requests';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { SelectBox } from 'src/components/SelectBox';
import { SideDropActions } from 'src/components/SideDropActions';
import Table from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import { logoutHelper } from 'src/helpers/logoutHelper';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import {
  Agent,
  getReferencedContextParams,
} from 'src/interfaces/agent.interface';
import { Number } from 'src/interfaces/number.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { Transcriber } from 'src/interfaces/transcriber.interface';
import { mapVoicePayload, Voice } from 'src/interfaces/voice.interface';
import { Webhook } from 'src/interfaces/webhook.interface';
import { limitString } from 'src/utils/limitString';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import { TestAgentModal } from './components/TestAgentModal';

const headers = [
  { key: 'id', label: 'ID', width: '8rem' },
  { key: 'label', label: 'Name', width: '30%' },
  { key: 'description', label: 'Description', width: '30%' },
  { key: 'inbound', label: 'Inbound', width: '5%', disableSorting: true },
  { key: 'source', label: 'Created By', width: '10%', disableSorting: true },
  { key: 'action', label: '', width: '30%', disableSorting: true },
];

const AGENT_SOURCES = [
  { label: 'All', value: '' },
  { label: 'User', value: 'user' },
  { label: 'Fluents', value: 'fluents' },
];

type Filter = {
  source: string;
  name: string;
  page: number;
  size: number;
  sort_column?: string;
  sort_desc?: boolean;
};

export const ListAgents = () => {
  const auth = getAuth();
  const { get, remove } = useAuthedRequest();
  const notification = useNotification();
  const { environment, environmentList } = useEnvironment();
  const { role } = useEnvironment();

  const envId = environment?.envId;

  const navigate = useNavigate();

  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [modalOpenDuplication, setModalOpenDuplication] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string | null>(
    String(envId),
  );
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useSessionStorageState<Filter>(
    '@control-plane/agent-filters',
    {
      defaultValue: {
        source: '',
        name: '',
        page: 1,
        size: 10,
        sort_column: undefined,
        sort_desc: undefined,
      },
    },
  );
  const debouncedFilter = useDebounce(filters, { wait: 500 });
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [contextObject, setContextObject] = useState<Record<string, string>>(
    {},
  );

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    const queryObject: QueryObject = {
      page: String(page),
      size: String(perPage),
      filters: JSON.stringify({
        source: filters?.source,
        name: filters?.name,
      }),
    };

    if (filters?.sort_column) {
      queryObject.sort_column = filters?.sort_column;
      queryObject.sort_desc = filters?.sort_desc ? 'true' : 'false';
    }

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/agents?${query.toString()}`);

    const inboundNumbers = (await fetchNumbers()) || [];

    const items = data.items.map((item: Agent) =>
      Object({
        ...item,
        source: item.user_id ? 'User' : 'Fluents',
        id: (
          <CopyableField value={String(item.id)} notification={notification} />
        ),
        name:
          item.name || item.label || getFirstAndLastFourUUID(String(item.id)),
        inbound: !!inboundNumbers.find(
          (number: Number) => number.inbound_agent === item.id,
        ) && (
          <div className="flex justify-center items-center">
            <Check />
          </div>
        ),
        description: limitString(item?.description || '', 50),
        action: (
          <div
            className="flex justify-center items-center"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <SideDropActions
              options={[
                {
                  label:
                    item.user_id && can(role, 'update', Subject.AGENTS)
                      ? 'Edit'
                      : 'View',
                  Icon: item.user_id ? Pencil : Eye,
                  show: !!item.user_id,
                  onClick: () => navigate('/agent/edit/' + item.id),
                },
                {
                  label: 'Duplicate agent',
                  Icon: UserSound,
                  onClick: () => openModalDuplicateAgent(item),
                },
                {
                  label: 'Test',
                  Icon: PhoneCall,
                  onClick: () => openModalOutboundCall(item),
                },
                {
                  label: 'Delete',
                  Icon: Trash,
                  color: 'error' as const,
                  show: !!item.user_id && can(role, 'delete', Subject.AGENTS),
                  onClick: () => openDeleteAgent(item),
                },
              ]}
            />
          </div>
        ),
      }),
    );

    setRows(items);
    setTotalItems(data.total);
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      setCurrentPage(filters?.page ?? 1);
      setSize(filters?.size ?? 10);
      fetchData(filters?.page ?? 1, filters?.size ?? 10);
    }
  }, [debouncedFilter]);

  const handlePageChange = (page: number, perPage: number) => {
    setFilters(
      (prev) =>
        ({
          ...prev,
          page,
          size: perPage,
        }) as Filter,
    );
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setFilters(
      (prev) =>
        ({
          ...prev,
          page: 1,
          size: perPage,
        }) as Filter,
    );
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setFilters(
      (prev) =>
        ({
          ...prev,
          sort_column: key,
          sort_desc: direction === 'desc',
        }) as Filter,
    );
  };

  const openModalOutboundCall = (agent: Agent) => {
    setCurrentAgent(agent);
    setIsTestModalOpen(true);
  };

  const fetchNumbers = async () => {
    try {
      const data = await get(`/numbers`);
      return data.items;
    } catch (error) {
      console.error('Failed to fetch numbers:', error);
    }
  };

  const openModalDuplicateAgent = async (agent: Agent) => {
    setCurrentAgent(agent);
    setModalOpenDuplication(true);
  };

  const closeModalDuplicateAgent = () => {
    setCurrentAgent(null);
    setSelectedEnvironment(String(envId));
    setModalOpenDuplication(false);
  };

  const duplicateAgentIntoEnvironment = async () => {
    if (!currentAgent || !selectedEnvironment) {
      notification.error(
        `No ${!currentAgent ? 'agent' : 'environment'} selected`,
      );
      return;
    }
    if (!(await canCreateAgent())) {
      return;
    }

    setLoading(true);

    try {
      let voiceToUse = {
        id: currentAgent.voice,
      } as Voice;

      let promptToUse = {
        id: currentAgent.prompt,
      } as Prompt;

      let webhookToUse: Webhook | undefined = {
        id: currentAgent.webhook,
      } as Webhook;

      let actionsToUse = currentAgent.actions as string[];

      if (envId !== selectedEnvironment) {
        const promptId =
          (currentAgent.prompt as Prompt)?.id || currentAgent.prompt;
        const fetchPrompt = await get(`/prompts/${promptId}`);
        if (!fetchPrompt) throw new Error('Failed to fetch prompt');

        promptToUse = { content: fetchPrompt.content } as Prompt;

        const fetchVoice = await get(`/voices/${currentAgent.voice}`);
        if (!fetchVoice) throw new Error('Failed to fetch voice');

        voiceToUse = mapVoicePayload(fetchVoice) as Voice;

        if (currentAgent.webhook) {
          const fetchWebhook = await get(`/webhooks/${currentAgent.webhook}`);
          if (fetchWebhook) {
            const webhookToSave = { ...fetchWebhook, id: undefined };
            const createWebhook = await post('/webhooks', webhookToSave, {
              envId: selectedEnvironment,
            });
            webhookToUse = createWebhook as Webhook;
          }
        }

        const actionsFetched = await Promise.all(
          actionsToUse.map(async (actionId) => {
            const fetchAction = await get(`/actions/${actionId}`);
            if (!fetchAction) throw new Error('Failed to fetch action');
            return fetchAction;
          }),
        );
        const actionsToSave = actionsFetched.map((action) => ({
          ...action,
          id: undefined,
        }));
        const actionsCreated = await Promise.all(
          actionsToSave.map((action) =>
            post(
              '/actions',
              { ...action, id: undefined },
              { envId: selectedEnvironment },
            ),
          ),
        );

        const newActions: string[] = actionsCreated.map(
          (createAction) => createAction.id,
        );

        actionsToUse = newActions;
      }

      const transcriber = currentAgent.transcriber as Transcriber;

      const clonedAgent = {
        ...currentAgent,
        name: `${currentAgent.name} - Copy`,
        prompt: promptToUse.id || promptToUse,
        voice: voiceToUse.id || voiceToUse,
        ...(webhookToUse && { webhook: webhookToUse.id || webhookToUse }),
        actions: actionsToUse,
        user_id: undefined,
        id: undefined,
        transcriber: { ...transcriber, id: undefined },
      };

      await post('/agents', clonedAgent, { envId: selectedEnvironment });

      if (envId === selectedEnvironment) {
        fetchData(currentPage, size);
      }

      notification.success('Agent duplicated successfully');
      setModalOpenDuplication(false);
    } catch (error) {
      notification.error('Failed to duplicate agent');
      Sentry.captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteAgent = (agent: Agent) => {
    setModalDeleteOpen(true);
    setSelectedId(String(agent.id));
  };

  const handleRemoveAgent = async () => {
    setLoading(true);
    try {
      await remove(`/agents/${selectedId}`);
      notification.success('Agent deleted successfully');
      fetchData(currentPage, size);
      setModalDeleteOpen(false);
    } catch {
      notification.error('Failed to delete agent');
    } finally {
      setLoading(false);
    }
  };

  const canCreateAgent = async () => {
    const resp = await auth.currentUser?.getIdTokenResult();
    if (!resp) {
      notification.error(`Auth expired`);
      return logoutHelper();
    }
    const { id } = resp.claims;

    const plan = await get(`/users/${id}/plan`);

    if (plan) {
      const maxAgents = plan?.featureGates?.agent;
      if (maxAgents && totalItems >= maxAgents) {
        notification.error(
          `The ${plan.name} plan is limited to ${maxAgents} agent(s). Please upgrade to add more agents`,
        );
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    if (currentAgent) {
      const referencedContextParams = getReferencedContextParams(currentAgent);
      setContextObject(
        referencedContextParams.reduce(
          (acc, param) => {
            acc[param] = '';
            return acc;
          },
          {} as Record<string, string>,
        ),
      );
    }
  }, [
    currentAgent?.prompt,
    currentAgent?.initial_message,
    currentAgent?.voicemail_message,
  ]);

  return (
    <div className="flex-1">
      <Heading
        title="Agents"
        subtitle="Agents define the behavior of your bot and are made out of prompts and voices. You can set them up here."
      >
        <div className="flex">
          <Button
            className="w-42 border-none flex items-center justify-center text-neutral-500"
            variant="outlined"
            color="default"
            href="https://docs.fluents.ai/api-reference/agents"
            target="_blank"
          >
            View help doc
            <FileText className="ml-2" size={18} />
          </Button>
        </div>
      </Heading>

      <Can operation="read" subject={Subject.AGENTS}>
        <ContentWrapper>
          <div className="flex flex-col md:flex-row justify-between mb-6">
            <div className="flex w-full md:w-1/2 self-end">
              <div className="flex w-full md:w-96 self-end">
                <Input
                  type="text"
                  required
                  placeholder="Search agents"
                  value={filters?.name}
                  onChange={(e) =>
                    setFilters(
                      (prev) =>
                        ({
                          ...prev,
                          name: e.target.value,
                        }) as Filter,
                    )
                  }
                  icon={<MagnifyingGlass size={20} />}
                  iconPosition="left"
                  inputAction={() => fetchData(currentPage, size)}
                  disabled={loading}
                />
              </div>
              <div className="w-40 ml-2">
                <Label htmlFor="agent-source">Created By</Label>
                <SelectBox
                  key="agent-source"
                  options={AGENT_SOURCES}
                  defaultValue={
                    filters?.source
                      ? AGENT_SOURCES.find(
                          (agentSource) =>
                            agentSource.value === filters?.source,
                        )
                      : { label: 'All', value: '' }
                  }
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onChange={(value: string) => {
                    setFilters(
                      (prev) =>
                        ({
                          ...prev,
                          source: value,
                        }) as Filter,
                    );
                    handlePageChange(1, size);
                  }}
                  className="col-span-3"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Can operation="create" subject={Subject.AGENTS}>
                <Button
                  onClick={async () => {
                    if (!(await canCreateAgent())) {
                      return;
                    }
                    navigate('/agent/new');
                  }}
                >
                  Create Agent
                </Button>
              </Can>
            </div>
          </div>

          <Table
            headers={headers}
            rows={rows}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={size}
            onRowClick={(index: number) => {
              const agent = rows[index] as unknown as Agent;
              if (agent) {
                navigate(
                  `/agent/edit/${(agent.id as unknown as { props: { value: string } }).props.value}`,
                );
              }
            }}
          />

          <TestAgentModal
            isOpen={isTestModalOpen}
            onClose={() => {
              setIsTestModalOpen(false);
              setCurrentAgent(null);
            }}
            agent={currentAgent ? { ...currentAgent, changed: false } : null}
            callContext={contextObject}
            setCallContext={setContextObject}
          />

          {/* Duplicate Agent Modal */}
          <Modal
            title={`Duplicate Agent - ${currentAgent?.name}`}
            isOpen={modalOpenDuplication}
            onClose={() => closeModalDuplicateAgent()}
            actionButton={
              <Button
                disabled={
                  loading || currentAgent === null || !selectedEnvironment
                }
                onClick={() =>
                  currentAgent ? duplicateAgentIntoEnvironment() : null
                }
              >
                Duplicate
              </Button>
            }
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <div className="mb-6">
                  <Label htmlFor="environment">Environment</Label>

                  <SelectBox
                    key={selectedEnvironment}
                    options={environmentList.map((env) => ({
                      label: env.name,
                      value: env.id,
                    }))}
                    defaultValue={{
                      label: environmentList.find(
                        (env) => env.id === selectedEnvironment,
                      )?.name,
                      value: String(selectedEnvironment),
                    }}
                    variant="outlined"
                    color="primary"
                    size="medium"
                    className="min-w-[140px]"
                    onChange={(value) => setSelectedEnvironment(value)}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </Modal>

          <Modal
            title="Delete Agent"
            isOpen={modalDeleteOpen}
            onClose={() => {
              setModalDeleteOpen(false);
              setSelectedId('');
            }}
            actionButton={
              <Button onClick={handleRemoveAgent}>Delete Agent</Button>
            }
            className="w-96"
          >
            <div className="text-center">
              Are you sure you want to delete this agent?
            </div>
          </Modal>
        </ContentWrapper>
      </Can>
    </div>
  );
};
